import { useCallback, useEffect, useRef, useState } from 'react'

const ROW_HEIGHT = 41
const ROW_HEIGHT_VARIABLE = '--admin-list-row-height'
const NON_TABLE_VERTICAL_SPACE = 184
const MIN_PAGE_SIZE = 5
const MAX_PAGE_SIZE = 100
const DEFAULT_HEADER_HEIGHT = 56
const EXTRA_SPACE = 4

const getEstimatedPageSize = () => Math.max(
  MIN_PAGE_SIZE,
  Math.floor((window.innerHeight - NON_TABLE_VERTICAL_SPACE) / ROW_HEIGHT)
)

const clampPageSize = value => Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, value))

const getRowHeight = container => {
  if (!container) return ROW_HEIGHT
  const value = Number.parseFloat(
    window.getComputedStyle(container).getPropertyValue(ROW_HEIGHT_VARIABLE)
  )
  return Number.isFinite(value) && value > 0 ? value : ROW_HEIGHT
}

export const usePageSize = () => {
  const tableRef = useRef(null)
  const [ pageSize, setPageSize ] = useState(() => clampPageSize(getEstimatedPageSize()))
  const [ isReady, setIsReady ] = useState(false)

  // The page size must only ever be derived from values that the rendered rows
  // cannot influence. Measuring the table container's own clientHeight (or the
  // height of a single row) creates a feedback loop: more rows make the element
  // taller, which raises the page size, which requests even more rows.
  const measure = useCallback(() => {
    const header = tableRef.current?.querySelector('.MuiTableHead-root')
    const headerHeight = header?.offsetHeight || DEFAULT_HEADER_HEIGHT
    const rowHeight = getRowHeight(tableRef.current)

    const reserved = NON_TABLE_VERTICAL_SPACE + headerHeight + EXTRA_SPACE
    const nextPageSize = clampPageSize(Math.floor((window.innerHeight - reserved) / rowHeight))
    setPageSize(prev => (prev === nextPageSize ? prev : nextPageSize))
    setIsReady(true)
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)

    return () => {
      window.removeEventListener('resize', measure)
    }
  }, [ measure ])

  return { pageSize, tableRef, isReady }
}
